import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LocalizationService } from '@abp/ng.core';
import { CouponService } from '@proxy/coupons';
import { CouponValidationResultDto } from '@proxy/coupons/models';
import { SelectedSommelierService } from '../../services/selected-sommelier.service';
import { finalize } from 'rxjs';
import { APP_ICONS } from '../../icons/app-icons';

@Component({
  selector: 'app-coupon-input',
  templateUrl: './coupon-input.component.html',
  standalone: false
})
export class CouponInputComponent implements OnInit {
  // Signals for Inputs and Outputs (Modern Angular)
  orderTotal = input<number>(0);
  sommelierId = input<string | undefined>();
  // Cliente a nombre de quien se arma el pedido (backoffice, #394). Sin esto se valida contra el usuario logueado.
  clientUserId = input<string | undefined>();
  items = input<any[]>([]); // New input for cart items
  compact = input<boolean>(false);
  // Clave de sessionStorage para persistir/restaurar el cupón aplicado. Cada contexto que use
  // este componente (checkout de cliente, alta de pedido desde backoffice, etc.) debe pasar la
  // suya propia — si comparten clave, aplicar un cupón en un contexto lo deja "fantasma" en el otro.
  persistKey = input<string>('appliedCoupon');
  
  couponApplied = output<CouponValidationResultDto>();
  couponRemoved = output<void>();

  // State Signals
  code = signal<string>('');
  status = signal<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  discountAmount = signal<number>(0);
  errorMessage = signal<string>('');
  appliedCoupon = signal<CouponValidationResultDto | undefined>(undefined);

  // Sanitized Icons
  private sanitizer = inject(DomSanitizer);
  icons: Record<string, SafeHtml> = {
    check: this.sanitizer.bypassSecurityTrustHtml(APP_ICONS.check),
    close: this.sanitizer.bypassSecurityTrustHtml(APP_ICONS.close),
    alert: this.sanitizer.bypassSecurityTrustHtml(APP_ICONS.alert),
    tag: this.sanitizer.bypassSecurityTrustHtml(APP_ICONS.tag)
  };

  // Modern Dependency Injection
  private couponService = inject(CouponService);
  private sommelierService = inject(SelectedSommelierService);
  private l = inject(LocalizationService);

  ngOnInit(): void {
    this.restoreFromSession();
  }

  applyCoupon() {
    const currentCode = this.code().trim();
    if (!currentCode) return;

    this.status.set('validating');
    this.errorMessage.set('');

    const inputData = {
      code: currentCode,
      orderTotal: this.orderTotal(),
      sommelierId: this.sommelierId(),
      clientUserId: this.clientUserId(),
      items: this.items().map(i => ({
        wineId: i.wineId,
        unitPrice: i.unitPrice,
        quantity: i.quantity
      }))
    };

    // Lo que se validó: si cuando responde el servidor el contexto ya cambió (otro cliente, sommelier, ítems o total),
    // la respuesta es de otro pedido y se descarta (winepassport#394).
    const requestContext = this.validationContext();

    this.couponService.validate(inputData as any)
      .pipe(finalize(() => {
        this.status.set(this.appliedCoupon() ? 'valid' : (this.errorMessage() ? 'invalid' : 'idle'));
      }))
      .subscribe({
        next: (result) => {
          if (requestContext !== this.validationContext()) {
            this.status.set('invalid');
            this.errorMessage.set('El pedido cambió mientras se validaba el cupón. Volvé a aplicarlo.');
            return;
          }
          if (result.valid) {
            this.status.set('valid');
            this.appliedCoupon.set(result);
            this.discountAmount.set(result.discountAmount);
            this.saveToSession(result);
            this.couponApplied.emit(result);
          } else {
            this.status.set('invalid');
            this.errorMessage.set(result.message || this.l.instant('::InvalidCoupon'));
            this.appliedCoupon.set(undefined);
            this.discountAmount.set(0);
          }
        },
        error: (err) => {
          this.status.set('invalid');
          this.errorMessage.set(this.l.instant('::CouponValidationError'));
          console.error('Error validating coupon:', err);
        }
      });
  }

  /** Foto de las entradas que participan de la validación del cupón. */
  private validationContext(): string {
    return JSON.stringify([
      this.orderTotal(), this.sommelierId(), this.clientUserId(),
      this.items().map(i => [i.wineId, i.unitPrice, i.quantity])
    ]);
  }

  removeCoupon() {
    this.appliedCoupon.set(undefined);
    this.code.set('');
    this.status.set('idle');
    this.discountAmount.set(0);
    this.errorMessage.set('');
    sessionStorage.removeItem(this.persistKey());
    this.couponRemoved.emit();
  }

  /** Igual que removeCoupon(), pero para cuando el estado ya se invalidó del lado del padre y solo
   * hace falta que este componente refleje "sin cupón" (evita reprocesar un couponRemoved redundante). */
  reset(): void {
    this.appliedCoupon.set(undefined);
    this.code.set('');
    this.status.set('idle');
    this.discountAmount.set(0);
    this.errorMessage.set('');
    sessionStorage.removeItem(this.persistKey());
  }

  private saveToSession(result: CouponValidationResultDto) {
    sessionStorage.setItem(this.persistKey(), JSON.stringify({
      code: this.code(),
      result: result,
      timestamp: new Date().getTime()
    }));
  }

  private restoreFromSession() {
    const saved = sessionStorage.getItem(this.persistKey());
    if (saved) {
      try {
        const { code, result } = JSON.parse(saved);
        this.code.set(code);
        this.appliedCoupon.set(result);
        this.discountAmount.set(result.discountAmount);
        this.status.set('valid');
        this.couponApplied.emit(result);
      } catch (e) {
        sessionStorage.removeItem('appliedCoupon');
      }
    }
  }
}
